import axios from 'axios';

import router from '@/core/router';
import store from '@/store';
import { sendMessageToSentry } from '@/utilities/sentry.js';
import { resetToScoreMode, hashSiteKey } from '@/utilities/recaptcha-enterprise.js';
import { showCheckboxRecaptchaDialog } from '@/utilities/recaptcha-dialog.js';
import { getAuthStateFromUrl } from '@/utilities/authState';

const request = axios.create({
  baseURL: process.env.API_URL,
});

// Store for pending requests that need reCAPTCHA retry
const pendingRecaptchaRequests = new Map();

request.interceptors.request.use(
  async (config) => {
    config.headers['Content-Language'] = store.state.userLang;

    // Attach AuthState as a header so the backend no longer depends on Referer.
    const authState = getAuthStateFromUrl();
    if (authState) {
      config.headers['X-Auth-State'] = authState;
    }

    // Handle reCAPTCHA token in headers
    if (config.data?.recaptchaToken) {
      const { recaptchaToken, siteKey, ...restData } = config.data;
      
      // Add reCAPTCHA token to header
      config.headers['X-Recaptcha-Token'] = recaptchaToken;
      
      // Add hashed site key to header
      if (siteKey) {
        const hashedSiteKey = await hashSiteKey(siteKey);
        config.headers['X-Recaptcha-Site-Key'] = hashedSiteKey;
      }
      
      // Remove reCAPTCHA data from body
      config.data = restData;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

request.interceptors.response.use(
  (response) => {
    // Reset to score mode on successful response
    resetToScoreMode();
    return response;
  },
  async (error) => {
    if (!error.response) {
      store.commit('setInternalError', true);
      sendMessageToSentry('ApiNoErrorResponse', { level: 'error' });
      return Promise.reject(error);
    }

    // Handle 400 response for reCAPTCHA score too low
    if (error.response.status === 400 && !error.config._isRetry) {
      const originalRequest = error.config;
      originalRequest._isRetry = true;

      try {
        // Extract the action from the original request data
        let action = '';
        
        // Determine action based on the API endpoint
        const url = originalRequest.url;
        if (url.includes('startreauth')) action = 'startReauth';
        else if (url.includes('checkaccount')) action = 'checkAccount';
        else if (url.includes('usersigninauth')) action = 'signIn';
        else if (url.includes('createtmaccount')) action = 'signUp';
        else if (url.includes('passwordlesslinkaccount')) action = 'passwordlessLinkAccount';
        else if (url.includes('sociallinkaccount')) action = 'socialLinkAccount';
        else if (url.includes('passwordlesssendcode')) action = 'sendOTPCode';
        else if (url.includes('passwordlessverifycode')) action = 'verifyOTPCode';
        else if (url.includes('verifysendcode')) action = 'sendAccountVerificationCode';
        else if (url.includes('verifyverifycode')) action = 'verifyAccountVerificationCode';
        else if (url.includes('querystatebypostalcode')) action = 'queryStateByPostalCode';
        
        // console.log('Handling 400 response, showing checkbox reCAPTCHA with action:', action);
        
        // Show checkbox reCAPTCHA dialog with action
        const checkboxResult = await showCheckboxRecaptchaDialog(action);
        
        // console.log('Checkbox result received:', checkboxResult);
        
        if (checkboxResult) {
          // Parse the original request data
          let requestData;
          try {
            requestData = originalRequest.data ? JSON.parse(originalRequest.data) : {};
          } catch (e) {
            // If parsing fails, assume it's already an object
            requestData = originalRequest.data || {};
          }
          
          // Update request with new reCAPTCHA data
          requestData.recaptchaToken = checkboxResult.token;
          requestData.siteKey = checkboxResult.siteKey;
          
          // Set the data back
          originalRequest.data = requestData;
          
          // Ensure headers are set correctly for JSON
          originalRequest.headers['Content-Type'] = 'application/json';
          
          // Clear the transformed request flag to ensure interceptors run again
          delete originalRequest.transformRequest;
          delete originalRequest.transformResponse;
          
          // console.log('Retrying request with new reCAPTCHA data:', requestData);
          
          // Retry the request
          return request(originalRequest);
        }
      } catch (recaptchaError) {
        sendMessageToSentry('RecaptchaCheckboxRetryFail', {
          level: 'error',
          extra: [
            {
              name: 'error',
              value: recaptchaError.message,
            },
          ],
        });
      }
    }

    if (error.response.status >= 500 || error.response.data?.is_redirect_error_page === 1) {
      sendMessageToSentry(`RedirectToErrorPage-${error.response.config.url}`, {
        level: 'error',
        extra: [
          {
            name: 'errorCode',
            value: error.response.status,
          },
          {
            name: 'error',
            value: error.response,
          },
        ],
      });

      const errorCode = error.response.data?.error_page_code;
      const trackID = error.response.data?.track_id || null;
      if (errorCode) {
        router.push({
          name: 'error',
          query: { code: errorCode, track_id: trackID },
        });
      } else {
        router.push({ name: 'error' });
      }
    }

    return Promise.reject(error);
  },
);


export default request;
